<template>
  <div class="asset-page">
    <div class="paper-grain"></div>

    <header class="asset-header">
      <div class="brand-line">
        <img :src="logo" alt="绿数花旅" />
        <span>绿数花旅 · 数字作品档案</span>
        <span class="series-index">{{ work.seriesNo }} / 05</span>
      </div>
      <p class="eyebrow">DIGITAL FLORAL ARCHIVE</p>
      <h1>您的作品是永久数字资产</h1>
    </header>

    <main class="asset-main">
      <section class="hero-card">
        <div class="hero-frame">
          <img :src="work.cover" :alt="work.title" />
          <div class="archive-stamp">
            <span>数字源作</span>
            <strong>NO.{{ work.seriesNo }}</strong>
          </div>
        </div>
        <div class="hero-copy">
          <p>{{ work.subtitle }}</p>
          <h2>{{ work.title }}</h2>
          <div class="hero-divider"><span></span></div>
          <div class="hero-meta">
            <span>{{ work.author.name }}老师作品</span>
            <span>{{ work.createdAt }}</span>
          </div>
          <p class="hero-description">{{ work.description }}</p>
        </div>
      </section>

      <section class="asset-identity">
        <div>
          <span>作品档案号</span>
          <strong>{{ work.archiveNo }}</strong>
        </div>
        <div>
          <span>数字版本</span>
          <strong>{{ work.provenance.version }}</strong>
        </div>
        <div>
          <span>作品状态</span>
          <strong>开放二创</strong>
        </div>
      </section>

      <section class="content-card">
        <div class="section-heading">
          <span class="section-number">壹</span>
          <div>
            <p>传播数据</p>
            <h3>数字作品影响记录</h3>
          </div>
        </div>
        <div class="stats-grid">
          <button type="button" @click="openDetail('views')"><strong>{{ formatNumber(work.stats.views) }}</strong><span>浏览</span><i>查看 ›</i></button>
          <button type="button" @click="openDetail('likes')"><strong>{{ formatNumber(work.stats.likes) }}</strong><span>点赞</span><i>查看 ›</i></button>
          <button type="button" @click="openDetail('comments')"><strong>{{ formatNumber(work.stats.comments) }}</strong><span>评论</span><i>查看 ›</i></button>
          <button type="button" @click="openDetail('rewards')"><strong>{{ formatCurrency(work.stats.rewards) }}</strong><span>打赏</span><i>查看 ›</i></button>
        </div>
      </section>

      <section class="content-card">
        <div class="section-heading">
          <span class="section-number">贰</span>
          <div>
            <p>二创记录</p>
            <h3>版本与衍生作品</h3>
          </div>
        </div>
        <div class="remix-overview">
          <button type="button" @click="openDetail('editors')"><strong>{{ work.remix.editors }}</strong><span>参与编辑者</span><i>查看记录 ›</i></button>
          <button type="button" @click="openDetail('versions')"><strong>{{ work.remix.versions }}</strong><span>维护版本</span><i>查看记录 ›</i></button>
          <button type="button" @click="openDetail('derivatives')"><strong>{{ work.remix.derivatives }}</strong><span>二创作品</span><i>查看记录 ›</i></button>
        </div>
        <div class="version-line">
          <span class="pulse-dot"></span>
          <div><small>当前公开版本</small><strong>{{ work.remix.latest }}</strong></div>
          <button class="citation-trigger" type="button" @click="openCitation">可引用</button>
        </div>
      </section>

      <section class="profile-card content-card">
        <div class="section-heading">
          <span class="section-number">叁</span>
          <div>
            <p>作品档案</p>
            <h3>作者档案</h3>
          </div>
        </div>
        <figure v-if="work.authorPhoto" class="author-photo">
          <img :src="work.authorPhoto" :alt="`${work.author.name}老师创作现场`" />
          <figcaption>
            <strong>{{ work.author.name }}</strong>
            <span>《{{ work.title }}》创作现场</span>
          </figcaption>
        </figure>
        <div class="author-row">
          <div class="author-seal">{{ work.author.name.slice(0, 1) }}</div>
          <div>
            <strong>{{ work.author.name }}</strong>
            <span>{{ work.author.title }}</span>
          </div>
        </div>
        <p class="body-copy">{{ work.author.profile }}</p>
      </section>

      <section id="craft-archive" class="craft-card content-card">
        <div class="section-heading">
          <span class="section-number">肆</span>
          <div>
            <p>技艺素材</p>
            <h3>花材结构与创作步骤</h3>
          </div>
        </div>
        <div class="craft-summary">
          <div><span>器皿</span><strong>{{ work.container }}</strong></div>
          <div><span>花材总数</span><strong>{{ work.flowerCount }} 枝</strong></div>
          <div><span>构成技法</span><strong>{{ work.technique || "中式插花" }}</strong></div>
        </div>
        <p class="structure-copy">{{ work.structure }}</p>
        <div class="material-list">
          <span v-for="item in work.materials" :key="item.name">
            {{ item.name }} <b>×{{ item.count }}</b>
          </span>
        </div>
        <ol class="step-list">
          <li v-for="(step, index) in work.steps" :key="step">
            <span>{{ String(index + 1).padStart(2, "0") }}</span>
            <p>{{ step }}</p>
          </li>
        </ol>
        <div class="media-strip">
          <div>
            <span class="media-icon">▷</span>
            <p>创作过程视频</p>
            <b>01:28</b>
            <span class="media-status">未授权</span>
          </div>
          <div>
            <span class="media-icon">▧</span>
            <p>作品真实图册</p>
            <b>8 张</b>
            <span class="media-status">未授权</span>
          </div>
        </div>
      </section>

      <section class="provenance-card content-card">
        <div class="section-heading light">
          <span class="section-number">伍</span>
          <div>
            <p>权属溯源</p>
            <h3>源作与引用关系</h3>
          </div>
        </div>
        <dl>
          <div><dt>权利主体</dt><dd>{{ work.provenance.owner }}</dd></div>
          <div><dt>来源属性</dt><dd>{{ work.provenance.sourceType }}</dd></div>
          <div><dt>开放范围</dt><dd>{{ work.provenance.rights }}</dd></div>
          <div><dt>引用次数</dt><dd>{{ work.provenance.cited }} 次</dd></div>
          <div><dt>登记时间</dt><dd>{{ work.provenance.recordedAt }}</dd></div>
          <div><dt>档案维护</dt><dd>{{ work.provenance.maintainer }}</dd></div>
        </dl>
        <div class="registration-eligibility">
          <span>数据产权登记</span>
          <strong>可申请数据产权登记</strong>
          <small>作品档案与权属信息已具备登记基础</small>
        </div>
        <div class="trace-code">
          <span>TRACE ID</span>
          <strong>{{ work.id }}</strong>
        </div>
      </section>

    </main>

    <transition name="detail-sheet">
      <div v-if="activeDetail" class="detail-overlay" @click.self="closeDetail">
        <section class="detail-dialog" role="dialog" aria-modal="true" :aria-label="activeDetail.title">
          <header class="detail-header">
            <div>
              <p>{{ activeDetail.eyebrow }}</p>
              <h3>{{ activeDetail.title }}</h3>
              <span>{{ activeDetail.subtitle }}</span>
            </div>
            <button type="button" aria-label="关闭明细" @click="closeDetail">×</button>
          </header>
          <ul class="detail-list">
            <li v-for="(record, index) in activeDetail.records" :key="`${detailKey}-${index}`">
              <span class="record-avatar" :class="`avatar-tone-${index % 4}`">{{ recordAvatar(record) }}</span>
              <div class="record-copy">
                <strong>{{ recordName(record) }}</strong>
                <span>{{ record.time }}</span>
                <p v-if="record.note">{{ record.note }}</p>
              </div>
              <b v-if="record.tag">{{ record.tag }}</b>
            </li>
          </ul>
        </section>
      </div>
    </transition>

    <transition name="detail-sheet">
      <div v-if="citationOpen" class="detail-overlay" @click.self="closeCitation">
        <section class="detail-dialog citation-dialog" role="dialog" aria-modal="true" aria-label="作品引用授权">
          <header class="detail-header">
            <div>
              <p>引用授权</p>
              <h3>引用此数字作品</h3>
              <span>允许在论文、教学、展会中使用</span>
            </div>
            <button type="button" aria-label="关闭引用窗口" @click="closeCitation">×</button>
          </header>
          <div class="citation-body">
            <div class="citation-author">
              <span>作者</span>
              <strong>{{ work.author.name }}</strong>
              <small>作者 ID：{{ work.author.id }}</small>
            </div>
            <div class="citation-scope">
              <span>授权使用范围</span>
              <div>
                <b>论文引用</b>
                <b>教学使用</b>
                <b>展会展示</b>
              </div>
            </div>
            <label for="citation-link">带作者 ID 的引用链接</label>
            <div class="citation-link-row">
              <input id="citation-link" ref="citationLink" :value="citationLink" readonly />
              <button type="button" @click="copyCitationLink">{{ citationCopyLabel }}</button>
            </div>
            <p class="citation-notice">使用时请保留作者署名、作者 ID 与作品来源链接。</p>
          </div>
        </section>
      </div>
    </transition>

    <transition name="detail-sheet">
      <div v-if="coursePurchaseOpen" class="detail-overlay" @click.self="closeCoursePurchase">
        <section class="detail-dialog course-dialog" role="dialog" aria-modal="true" aria-label="购买数字课程">
          <header class="detail-header">
            <div>
              <p>数字课程授权</p>
              <h3>购买整套数字化课件</h3>
              <span>完整保留一件作品从真实创作到数字二创的全过程</span>
            </div>
            <button type="button" aria-label="关闭购买窗口" @click="closeCoursePurchase">×</button>
          </header>
          <div class="course-body">
            <div class="course-offer">
              <div>
                <span>课程总量</span>
                <strong>30<small>节</small></strong>
              </div>
              <div class="course-price">
                <span>单人学习授权</span>
                <strong><small>¥</small>39.9</strong>
              </div>
            </div>
            <p class="course-lead">一次购买，即可获得这件作品的整套数字化学习资料。</p>
            <div class="course-includes">
              <article>
                <b>影</b>
                <div><strong>真实创作过程视频</strong><span>完整呈现选材、构图、调整与定型过程</span></div>
              </article>
              <article>
                <b>册</b>
                <div><strong>作品高清图册</strong><span>包含成品、多角度展示与花材结构细节</span></div>
              </article>
              <article>
                <b>源</b>
                <div><strong>1:1 数字化源文件</strong><span>可进入编辑器拆解学习并继续数字二次创作</span></div>
              </article>
            </div>
            <div class="course-license">
              <span>授权说明</span>
              <p>课程按人授权，仅限购买者本人学习与二次创作使用；作者署名与数字源作权属保持不变。</p>
            </div>
          </div>
        </section>
      </div>
    </transition>

    <footer class="action-dock">
      <button class="secondary-action" type="button" @click="openCoursePurchase">
        购买数字课程
      </button>
      <button class="primary-action" type="button" @click="enterEditor">
        进入数字作品二次创作
        <i>→</i>
      </button>
    </footer>
  </div>
</template>

<script>
import logo from "@/assets/logo.png";
import { getTemplateShowcaseWork, templateShowcaseWorks } from "@/mock/templateShowcase";

const DETAIL_PANELS = {
  views: {
    eyebrow: "传播数据",
    title: "浏览记录",
    subtitle: "最近访问这件数字作品的用户",
    records: [
      { name: "林间小鹿", time: "今天 14:32", note: "通过作品二维码进入" },
      { name: "清风花语", time: "今天 13:18", note: "停留 3 分 26 秒" },
      { name: "南山艺舍", time: "昨天 20:45", note: "通过好友分享进入" },
      { name: "一枝春", time: "昨天 16:09", note: "再次浏览作品档案" },
      { name: "花间集", time: "09月13日 10:21", note: "浏览技艺素材" },
    ],
  },
  likes: {
    eyebrow: "传播数据",
    title: "点赞记录",
    subtitle: "最近为作品点赞的用户",
    records: [
      { name: "木槿", time: "今天 14:18", note: "为作品点赞", tag: "已点赞" },
      { name: "花艺研习社", time: "今天 11:06", note: "认可作品构成", tag: "已点赞" },
      { name: "山茶", time: "昨天 21:37", note: "为作品点赞", tag: "已点赞" },
      { name: "叶知秋", time: "09月13日 18:52", note: "为作品点赞", tag: "已点赞" },
    ],
  },
  comments: {
    eyebrow: "传播数据",
    title: "评论记录",
    subtitle: "围绕作品留下的交流内容",
    records: [
      { name: "听竹", time: "今天 12:26", note: "主枝走势很自然，整体重心也很稳。" },
      { name: "青禾", time: "昨天 19:40", note: "花材层次清楚，很适合作为课堂案例。" },
      { name: "若水", time: "昨天 15:08", note: "喜欢器皿与花材之间的比例关系。" },
      { name: "花影", time: "09月12日 09:36", note: "准备尝试自己的二创版本。" },
    ],
  },
  rewards: {
    eyebrow: "传播数据",
    title: "打赏记录",
    subtitle: "最近通过打赏支持作者的用户",
    records: [
      { name: "兰因", time: "今天 10:08", note: "感谢分享完整的创作结构", tag: "¥ 666" },
      { name: "拂晓", time: "昨天 18:24", note: "支持老师持续发布数字作品", tag: "¥ 388" },
      { name: "栖迟", time: "09月13日 14:42", note: "感谢开放作品用于学习", tag: "¥ 188" },
      { name: "半夏", time: "09月11日 16:30", note: "支持作品的数字化传承", tag: "¥ 88" },
    ],
  },
  editors: {
    eyebrow: "二创记录",
    title: "参与编辑者",
    subtitle: "最近使用源作进行二次创作的用户",
    records: [
      { name: "青禾", time: "今天 13:42", note: "创建二创草稿", tag: "编辑中" },
      { name: "花艺研习社", time: "昨天 20:16", note: "完成一次结构调整", tag: "已保存" },
      { name: "一枝春", time: "09月13日 15:38", note: "从源作创建新版本", tag: "已保存" },
      { name: "南山艺舍", time: "09月12日 11:05", note: "调整花材比例与方向", tag: "已完成" },
    ],
  },
  versions: {
    eyebrow: "二创记录",
    title: "维护版本",
    subtitle: "源作的静态版本演进记录",
    records: [
      { name: "$author", time: "2026年09月13日", note: "调整主枝比例，发布当前开放版本", tag: "当前版本" },
      { name: "$author", time: "2026年09月08日", note: "完善器皿位置与花材层次", tag: "V1.3" },
      { name: "$author", time: "2026年08月30日", note: "补充作品档案与材料清单", tag: "V1.2" },
      { name: "$author", time: "2026年08月28日", note: "首次登记数字源作", tag: "V1.0" },
    ],
  },
  derivatives: {
    eyebrow: "二创记录",
    title: "二创作品",
    subtitle: "基于这件源作生成的近期作品",
    records: [
      { name: "林间小鹿", time: "今天 15:02", note: "《向光而行》· 调整花材疏密", tag: "公开" },
      { name: "听竹", time: "昨天 19:28", note: "《秋日新序》· 更换器皿与背景", tag: "公开" },
      { name: "清风花语", time: "09月13日 17:46", note: "《一束暖阳》· 修改主枝方向", tag: "草稿" },
      { name: "云水花房", time: "09月12日 10:14", note: "《花间回响》· 精简辅助花材", tag: "公开" },
    ],
  },
};

export default {
  name: "TemplateIntro",
  data() {
    const routeWorkId = this.$route.query.productExpandId || this.$route.query.demoWork;
    return {
      logo,
      work: getTemplateShowcaseWork(routeWorkId) || templateShowcaseWorks[1],
      detailKey: "",
      citationOpen: false,
      coursePurchaseOpen: false,
      citationCopyLabel: "复制链接",
      citationCopyResetTimer: null,
    };
  },
  computed: {
    activeDetail() {
      return DETAIL_PANELS[this.detailKey] || null;
    },
    citationLink() {
      const href = this.$router.resolve({
        path: "/template-intro",
        query: {
          productExpandId: this.work.id,
          authorId: this.work.author.id,
          source: "citation",
        },
      }).href;
      return `${window.location.origin}${window.location.pathname}${href}`;
    },
  },
  watch: {
    "$route.query.productExpandId"() {
      this.syncWork();
    },
    "$route.query.demoWork"() {
      this.syncWork();
    },
  },
  created() {
    document.title = `${this.work.title}｜${this.work.author.name}数字作品`;
  },
  beforeDestroy() {
    if (this.citationCopyResetTimer) clearTimeout(this.citationCopyResetTimer);
  },
  methods: {
    syncWork() {
      const routeWorkId = this.$route.query.productExpandId || this.$route.query.demoWork;
      this.work = getTemplateShowcaseWork(routeWorkId) || templateShowcaseWorks[1];
      this.closeCitation();
      this.closeCoursePurchase();
      document.title = `${this.work.title}｜${this.work.author.name}数字作品`;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("zh-CN");
    },
    formatCurrency(value) {
      return `¥${Number(value || 0).toLocaleString("zh-CN")}`;
    },
    openDetail(key) {
      this.detailKey = key;
    },
    closeDetail() {
      this.detailKey = "";
    },
    openCitation() {
      this.citationCopyLabel = "复制链接";
      this.citationOpen = true;
    },
    closeCitation() {
      this.citationOpen = false;
      this.citationCopyLabel = "复制链接";
    },
    openCoursePurchase() {
      this.detailKey = "";
      this.closeCitation();
      this.coursePurchaseOpen = true;
    },
    closeCoursePurchase() {
      this.coursePurchaseOpen = false;
    },
    async copyCitationLink() {
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.citationLink);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }

      if (!copied && this.$refs.citationLink) {
        const input = this.$refs.citationLink;
        input.focus();
        input.select();
        input.setSelectionRange(0, input.value.length);
        copied = document.execCommand("copy");
      }

      this.citationCopyLabel = copied ? "已复制" : "请手动复制";
      if (this.citationCopyResetTimer) clearTimeout(this.citationCopyResetTimer);
      this.citationCopyResetTimer = setTimeout(() => {
        this.citationCopyLabel = "复制链接";
      }, 1800);
    },
    recordName(record) {
      return record.name === "$author" ? this.work.author.name : record.name;
    },
    recordAvatar(record) {
      return this.recordName(record).slice(0, 1);
    },
    enterEditor() {
      const query = { ...this.$route.query };
      const editorPath = query.editorPath === "/studio" ? "/studio" : "/practice";
      delete query.editorPath;
      delete query.demoWork;
      query.productExpandId = this.work.id;
      query.mode = "edit";
      query.enterEditor = "1";
      this.$router.push({ path: editorPath, query });
    },
  },
};
</script>

<style lang="scss" scoped>
.asset-page {
  --ink: #173f2c;
  --green: #1e6846;
  --green-deep: #12482f;
  --paper: #f3eee3;
  --brown: #76513f;
  position: relative;
  min-height: 100vh;
  padding-bottom: 112px;
  overflow-x: hidden;
  color: var(--ink);
  background:
    radial-gradient(circle at 8% 16%, rgba(175, 139, 102, 0.09), transparent 26%),
    radial-gradient(circle at 92% 44%, rgba(48, 108, 76, 0.08), transparent 32%),
    var(--paper);
  font-family: "Songti SC", "STSong", "Noto Serif SC", serif;
}

.paper-grain {
  position: fixed;
  inset: 0;
  pointer-events: none;
  opacity: 0.38;
  background-image:
    repeating-linear-gradient(8deg, rgba(85, 61, 43, 0.025) 0, rgba(85, 61, 43, 0.025) 1px, transparent 1px, transparent 4px),
    repeating-linear-gradient(96deg, rgba(255, 255, 255, 0.18) 0, rgba(255, 255, 255, 0.18) 1px, transparent 1px, transparent 6px);
}

.asset-header,
.asset-main {
  position: relative;
  z-index: 1;
  width: min(100% - 32px, 720px);
  margin: 0 auto;
}

.asset-header {
  padding: 24px 0 22px;
  text-align: center;
  border-bottom: 1px solid rgba(29, 104, 70, 0.48);

  .brand-line {
    display: flex;
    align-items: center;
    gap: 8px;
    color: rgba(23, 63, 44, 0.76);
    font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif;
    font-size: 12px;
    letter-spacing: 0.08em;

    img {
      width: 25px;
      height: 25px;
      object-fit: contain;
    }

    .series-index {
      margin-left: auto;
      font-weight: 700;
      color: var(--green);
    }
  }

  .eyebrow {
    margin: 24px 0 8px;
    color: var(--brown);
    font: 600 10px/1.4 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif;
    letter-spacing: 0.28em;
  }

  h1 {
    margin: 0;
    color: #1a291f;
    font-size: clamp(25px, 7.2vw, 40px);
    font-weight: 500;
    letter-spacing: 0.12em;
  }

}

.asset-main {
  padding-top: 20px;
}

.hero-card {
  overflow: hidden;
  border: 1px solid rgba(77, 56, 40, 0.2);
  border-radius: 6px 6px 30px 6px;
  background: rgba(255, 252, 246, 0.74);
  box-shadow: 0 18px 42px rgba(63, 45, 32, 0.11);
}

.hero-frame {
  position: relative;
  margin: 10px;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  border: 1px solid #282019;
  background: #ebe4d9;

  > img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
}

.archive-stamp {
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 74px;
  height: 74px;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 50%;
  color: #fff;
  background: rgba(24, 92, 61, 0.9);
  backdrop-filter: blur(5px);
  transform: rotate(-8deg);

  span { font-size: 11px; letter-spacing: 0.08em; }
  strong { margin-top: 3px; font: 700 13px/1.2 -apple-system, BlinkMacSystemFont, sans-serif; }
}

.hero-copy {
  padding: 8px 22px 24px;
  text-align: center;

  > p:first-child {
    margin: 0 0 5px;
    color: var(--brown);
    font-size: 12px;
    letter-spacing: 0.18em;
  }

  h2 {
    margin: 0;
    color: #4c2c20;
    font-size: 30px;
    font-weight: 500;
    letter-spacing: 0.16em;
  }
}

.hero-divider {
  display: flex;
  justify-content: center;
  margin: 12px 0;

  span {
    width: 62px;
    height: 1px;
    background: var(--green);
  }
}

.hero-meta {
  display: flex;
  justify-content: center;
  gap: 18px;
  color: #6d5e50;
  font-size: 12px;
}

.hero-description,
.body-copy,
.structure-copy {
  margin: 16px 0 0;
  color: #5f594f;
  font: 14px/1.9 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif;
  text-align: justify;
}

.asset-identity {
  display: grid;
  grid-template-columns: 1.5fr 0.8fr 0.9fr;
  margin: 14px 0 22px;
  overflow: hidden;
  border-radius: 14px;
  background: var(--green);
  box-shadow: 0 10px 26px rgba(24, 90, 59, 0.18);

  div {
    min-width: 0;
    padding: 13px 12px;
    border-right: 1px solid rgba(255, 255, 255, 0.18);
    color: #fff;
  }

  div:last-child { border-right: 0; }
  span { display: block; margin-bottom: 4px; opacity: 0.68; font-size: 10px; }
  strong { display: block; overflow: hidden; font-size: 12px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
}

.content-card {
  margin-bottom: 16px;
  padding: 22px 18px;
  border: 1px solid rgba(41, 90, 64, 0.13);
  border-radius: 24px 6px 24px 6px;
  background: rgba(255, 253, 248, 0.8);
  box-shadow: 0 10px 28px rgba(63, 45, 32, 0.07);
  scroll-margin-top: 16px;
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;

  .section-number {
    display: grid;
    width: 38px;
    height: 38px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 50% 50% 50% 10%;
    color: #fff;
    background: var(--green);
    font-size: 17px;
  }

  p { margin: 0 0 2px; color: #8a6b56; font-size: 11px; letter-spacing: 0.18em; }
  h3 { margin: 0; color: var(--ink); font-size: 20px; font-weight: 500; letter-spacing: 0.08em; }
}

.author-row {
  display: flex;
  align-items: center;
  gap: 12px;

  .author-seal {
    display: grid;
    width: 48px;
    height: 48px;
    place-items: center;
    border: 1px solid #9b5d43;
    color: #9b5d43;
    background: #fffaf1;
    font-size: 25px;
  }

  strong,
  span { display: block; }
  strong { color: #3d2d23; font-size: 18px; font-weight: 500; }
  span { margin-top: 3px; color: #8b7b6d; font: 12px/1.4 -apple-system, BlinkMacSystemFont, sans-serif; }
}

.author-photo {
  position: relative;
  margin: 0 0 18px;
  overflow: hidden;
  border: 1px solid rgba(41, 90, 64, 0.16);
  border-radius: 5px 20px 5px 20px;
  background: #2f2119;
  box-shadow: 0 10px 24px rgba(54, 36, 24, 0.14);

  img {
    display: block;
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }

  figcaption {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    padding: 30px 14px 12px;
    color: #fff;
    background: linear-gradient(transparent, rgba(24, 15, 10, 0.82));

    strong { font-size: 17px; font-weight: 500; letter-spacing: 0.08em; }
    span { font: 11px/1.4 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif; opacity: 0.8; }
  }
}

.craft-summary,
.remix-overview {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  div,
  button {
    min-width: 0;
    padding: 12px 8px;
    text-align: center;
    border-radius: 14px;
    background: rgba(31, 104, 70, 0.08);
  }

  button {
    border: 0;
    color: inherit;
    font-family: inherit;
    cursor: pointer;
    transition: transform 0.18s ease, background 0.18s ease;
  }

  button:active { transform: scale(0.97); }

  span,
  strong { display: block; }
  span { color: #83776c; font: 10px/1.35 -apple-system, BlinkMacSystemFont, sans-serif; }
  strong { margin-top: 5px; color: var(--green-deep); font-size: 14px; font-weight: 600; }
  i { display: block; margin-top: 6px; color: #9b7258; font: normal 9px/1.2 -apple-system, BlinkMacSystemFont, sans-serif; }
}

.material-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0;

  span {
    padding: 7px 10px;
    border: 1px solid rgba(35, 105, 72, 0.2);
    border-radius: 999px;
    color: #46584b;
    background: #f8f5ec;
    font: 12px/1 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif;
  }

  b { color: #a06443; font-weight: 600; }
}

.step-list {
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 40px;
    border-bottom: 1px dashed rgba(37, 89, 61, 0.16);
  }

  li > span { color: #a07256; font: 600 11px/1 -apple-system, BlinkMacSystemFont, sans-serif; }
  p { margin: 0; color: #4b514a; font: 13px/1.5 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif; }
}

.media-strip {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 16px;

  > div {
    position: relative;
    display: grid;
    grid-template-columns: 30px 1fr;
    grid-template-rows: 1fr 1fr;
    padding: 12px;
    border-radius: 14px;
    color: #fff;
    background: var(--green);
  }

  .media-icon { grid-row: 1 / 3; align-self: center; font-size: 22px; }
  p, b { margin: 0; font: 12px/1.3 -apple-system, BlinkMacSystemFont, sans-serif; }
  b { opacity: 0.62; font-size: 10px; font-weight: 400; }
  .media-status {
    position: absolute;
    right: 10px;
    bottom: 10px;
    padding: 3px 6px;
    border: 1px solid rgba(255, 255, 255, 0.42);
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.88);
    background: rgba(255, 255, 255, 0.1);
    font: 9px/1 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif;
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);

  button {
    min-width: 0;
    padding: 5px 2px;
    border: 0;
    text-align: center;
    border-right: 1px solid rgba(28, 91, 61, 0.13);
    color: inherit;
    background: transparent;
    font-family: inherit;
    cursor: pointer;
  }

  button:last-child { border-right: 0; }
  strong, span { display: block; }
  strong { color: var(--green-deep); font: 600 18px/1.3 -apple-system, BlinkMacSystemFont, sans-serif; }
  span { margin-top: 5px; color: #8c7c6c; font-size: 10px; }
  i { display: block; margin-top: 6px; color: #a0795f; font: normal 8px/1 -apple-system, BlinkMacSystemFont, sans-serif; }
  button:active { transform: scale(0.96); }
}

.remix-overview strong { font: 600 24px/1.1 -apple-system, BlinkMacSystemFont, sans-serif; }

.version-line {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f3eee3;

  .pulse-dot { width: 9px; height: 9px; border-radius: 50%; background: #60a43b; box-shadow: 0 0 0 5px rgba(96, 164, 59, 0.14); }
  div { flex: 1; }
  small, strong { display: block; }
  small { color: #948678; font-size: 9px; }
  strong { margin-top: 2px; color: #3c4d40; font-size: 13px; font-weight: 500; }
  .citation-trigger {
    padding: 7px 11px;
    border: 0;
    border-radius: 999px;
    color: #fff;
    background: var(--green);
    font: 500 10px/1 -apple-system, BlinkMacSystemFont, sans-serif;
    cursor: pointer;
  }

  .citation-trigger:active { transform: scale(0.96); }
}

.provenance-card {
  color: #fff;
  background: linear-gradient(145deg, #1b6845, #123e2b);
  box-shadow: 0 16px 34px rgba(18, 62, 43, 0.22);

  .section-heading.light {
    .section-number { color: var(--green); background: #f5efe3; }
    p { color: rgba(255, 255, 255, 0.58); }
    h3 { color: #fff; }
  }

  dl { margin: 0; }
  dl > div { display: flex; justify-content: space-between; gap: 15px; padding: 9px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.12); }
  dt { color: rgba(255, 255, 255, 0.6); font-size: 11px; }
  dd { margin: 0; text-align: right; font-size: 12px; }
}

.registration-eligibility {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;
  margin-top: 16px;
  padding: 14px 15px 14px 48px;
  overflow: hidden;
  border: 1px solid rgba(244, 206, 126, 0.42);
  border-radius: 5px 18px 5px 18px;
  background: linear-gradient(115deg, rgba(244, 206, 126, 0.18), rgba(255, 255, 255, 0.07));

  &::before {
    content: "登";
    position: absolute;
    top: 50%;
    left: 13px;
    display: grid;
    width: 25px;
    height: 25px;
    place-items: center;
    border: 1px solid rgba(255, 227, 169, 0.72);
    border-radius: 50%;
    color: #ffe3a9;
    font-size: 13px;
    transform: translateY(-50%);
  }

  span {
    grid-column: 1 / -1;
    color: rgba(255, 227, 169, 0.72);
    font: 9px/1.2 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif;
    letter-spacing: 0.12em;
  }

  strong {
    color: #fff1cd;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.05em;
  }

  small {
    align-self: center;
    color: rgba(255, 255, 255, 0.58);
    font: 9px/1.4 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif;
    text-align: right;
  }
}

.trace-code {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 11px 12px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);

  span { color: rgba(255, 255, 255, 0.58); font: 9px/1 -apple-system, BlinkMacSystemFont, sans-serif; letter-spacing: 0.16em; }
  strong { min-width: 0; overflow: hidden; font: 500 11px/1.3 ui-monospace, SFMono-Regular, Menlo, monospace; text-overflow: ellipsis; }
}

.detail-overlay {
  position: fixed;
  z-index: 60;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 18px 16px calc(18px + env(safe-area-inset-bottom));
  background: rgba(17, 28, 22, 0.52);
  backdrop-filter: blur(4px);
}

.detail-dialog {
  width: min(100%, 560px);
  max-height: min(76vh, 680px);
  overflow: hidden;
  border: 1px solid rgba(39, 91, 63, 0.16);
  border-radius: 28px 8px 28px 8px;
  background: #f8f4eb;
  box-shadow: 0 26px 70px rgba(13, 32, 22, 0.3);
}

.citation-body { padding: 18px 20px 22px; }

.citation-author {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;
  padding: 14px;
  border-radius: 14px;
  background: rgba(30, 104, 70, 0.07);

  span { grid-row: 1 / 3; align-self: center; color: #97735d; font-size: 10px; letter-spacing: 0.12em; }
  strong { color: var(--green-deep); font-size: 16px; font-weight: 600; }
  small { color: #7e776e; font: 10px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace; }
}

.citation-scope {
  margin-top: 18px;

  > span { color: #7d6f63; font-size: 10px; letter-spacing: 0.08em; }
  > div { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 9px; }
  b { padding: 7px 10px; border-radius: 999px; color: var(--green); background: #e4ecdf; font: 500 11px/1 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif; }
}

.citation-body > label {
  display: block;
  margin-top: 18px;
  color: #7d6f63;
  font-size: 10px;
  letter-spacing: 0.08em;
}

.citation-link-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  margin-top: 8px;

  input {
    min-width: 0;
    height: 42px;
    padding: 0 12px;
    border: 1px solid rgba(31, 95, 64, 0.18);
    border-radius: 10px;
    color: #4d5a50;
    background: rgba(255, 255, 255, 0.7);
    font: 10px/1 ui-monospace, SFMono-Regular, Menlo, monospace;
  }

  button {
    min-width: 76px;
    padding: 0 13px;
    border: 0;
    border-radius: 10px;
    color: #fff;
    background: var(--green);
    font: 500 11px/1 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif;
  }
}

.citation-notice {
  margin: 12px 0 0;
  color: #8d8175;
  font: 10px/1.6 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif;
}

.course-dialog {
  background:
    radial-gradient(circle at 94% 4%, rgba(226, 139, 49, 0.14), transparent 34%),
    #f8f4eb;
}

.course-body {
  max-height: calc(min(76vh, 680px) - 126px);
  padding: 18px 20px 22px;
  overflow-y: auto;
}

.course-offer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;
  border-radius: 20px 6px 20px 6px;
  color: #fff;
  background: linear-gradient(135deg, #1f704c, #14452f);
  box-shadow: 0 12px 26px rgba(20, 69, 47, 0.18);

  > div {
    padding: 17px 18px;
    border-right: 1px solid rgba(255, 255, 255, 0.15);
  }

  > div:last-child { border-right: 0; }
  span { display: block; color: rgba(255, 255, 255, 0.64); font: 10px/1.4 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif; }
  strong { display: block; margin-top: 4px; font: 600 30px/1.1 -apple-system, BlinkMacSystemFont, sans-serif; }
  strong small { margin-left: 3px; font-size: 12px; font-weight: 400; }

  .course-price {
    background: linear-gradient(135deg, rgba(232, 147, 51, 0.94), rgba(198, 96, 30, 0.96));

    strong small { margin: 0 3px 0 0; font-size: 15px; }
  }
}

.course-lead {
  margin: 16px 0;
  color: #665d52;
  font: 12px/1.7 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif;
  text-align: center;
}

.course-includes {
  display: grid;
  gap: 9px;

  article {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    border: 1px solid rgba(31, 104, 70, 0.11);
    border-radius: 14px 4px 14px 4px;
    background: rgba(255, 255, 255, 0.58);
  }

  article > b {
    display: grid;
    width: 34px;
    height: 34px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 50% 50% 50% 8px;
    color: #fff;
    background: var(--green);
    font-size: 15px;
    font-weight: 500;
  }

  article div { min-width: 0; }
  strong { display: block; color: #294936; font-size: 13px; font-weight: 600; }
  span { display: block; margin-top: 3px; color: #887b6e; font: 10px/1.5 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif; }
}

.course-license {
  margin-top: 14px;
  padding: 12px 13px;
  border-left: 3px solid #d67b2a;
  border-radius: 3px 12px 12px 3px;
  background: rgba(222, 136, 47, 0.08);

  span { color: #a2602d; font-size: 10px; letter-spacing: 0.08em; }
  p { margin: 5px 0 0; color: #756b61; font: 10px/1.65 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif; }
}

.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 20px 16px;
  border-bottom: 1px solid rgba(31, 95, 64, 0.12);

  p { margin: 0 0 3px; color: #987158; font-size: 10px; letter-spacing: 0.18em; }
  h3 { margin: 0; color: var(--green-deep); font-size: 24px; font-weight: 500; letter-spacing: 0.08em; }
  span { display: block; margin-top: 5px; color: #8b7d70; font: 11px/1.5 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif; }
  button {
    width: 36px;
    height: 36px;
    flex: 0 0 auto;
    border: 1px solid rgba(31, 95, 64, 0.16);
    border-radius: 50%;
    color: var(--green-deep);
    background: rgba(255, 255, 255, 0.62);
    font: 300 25px/1 -apple-system, BlinkMacSystemFont, sans-serif;
  }
}

.detail-list {
  max-height: calc(min(76vh, 680px) - 150px);
  margin: 0;
  padding: 4px 20px;
  overflow-y: auto;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 0;
    border-bottom: 1px dashed rgba(37, 89, 61, 0.15);
  }

  li:last-child { border-bottom: 0; }
  li > b { margin-left: auto; padding: 5px 8px; border-radius: 999px; color: #fff; background: var(--green); font: 500 9px/1 -apple-system, BlinkMacSystemFont, sans-serif; white-space: nowrap; }
}

.record-avatar {
  display: grid;
  width: 44px;
  height: 44px;
  flex: 0 0 auto;
  place-items: center;
  border: 2px solid rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  color: #fff;
  background: linear-gradient(145deg, #4d8e68, #1e5d40);
  box-shadow: 0 4px 12px rgba(25, 70, 48, 0.18);
  font-size: 17px;
}

.avatar-tone-1 { background: linear-gradient(145deg, #a87b5c, #714630); }
.avatar-tone-2 { background: linear-gradient(145deg, #719397, #3c6267); }
.avatar-tone-3 { background: linear-gradient(145deg, #8b786b, #57483f); }

.record-copy {
  min-width: 0;
  flex: 1;

  strong { display: inline; color: #33473a; font-size: 14px; font-weight: 600; }
  > span { margin-left: 8px; color: #a09182; font: 9px/1.4 -apple-system, BlinkMacSystemFont, sans-serif; }
  p { margin: 5px 0 0; overflow: hidden; color: #6b645b; font: 11px/1.5 -apple-system, BlinkMacSystemFont, "PingFang SC", sans-serif; text-overflow: ellipsis; white-space: nowrap; }
}

.detail-sheet-enter-active,
.detail-sheet-leave-active { transition: opacity 0.2s ease; }
.detail-sheet-enter-active .detail-dialog,
.detail-sheet-leave-active .detail-dialog { transition: transform 0.22s ease; }
.detail-sheet-enter,
.detail-sheet-leave-to { opacity: 0; }
.detail-sheet-enter .detail-dialog,
.detail-sheet-leave-to .detail-dialog { transform: translateY(24px); }

.action-dock {
  position: fixed;
  z-index: 20;
  right: 0;
  bottom: 0;
  left: 0;
  display: grid;
  grid-template-columns: 0.78fr 1.4fr;
  gap: 10px;
  padding: 12px max(16px, env(safe-area-inset-right)) calc(12px + env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
  border-top: 1px solid rgba(31, 94, 65, 0.13);
  background: rgba(247, 243, 234, 0.94);
  box-shadow: 0 -12px 32px rgba(49, 39, 29, 0.1);
  backdrop-filter: blur(14px);

  button {
    height: 58px;
    border-radius: 18px 4px 18px 4px;
    font-family: "Songti SC", "STSong", serif;
  }
}

.secondary-action {
  border: 1px solid rgba(29, 103, 69, 0.28);
  color: var(--green-deep);
  background: #f8f4eb;
  font-size: 14px;

  span { display: block; margin-bottom: 2px; color: #9a735b; font-size: 9px; }
}

.primary-action {
  position: relative;
  border: 0;
  padding: 0 42px 0 12px;
  color: #fff;
  background: linear-gradient(135deg, #e69036, #c76520);
  box-shadow: 0 8px 20px rgba(183, 85, 24, 0.28);
  font-size: 16px;
  letter-spacing: 0.01em;
  white-space: nowrap;

  i { position: absolute; right: 18px; top: 20px; font: normal 18px/1 -apple-system, BlinkMacSystemFont, sans-serif; }
}

@media (min-width: 760px) {
  .asset-page { padding-bottom: 124px; }
  .hero-card { display: grid; grid-template-columns: 1fr 0.9fr; align-items: center; }
  .hero-copy { padding: 30px; }
  .action-dock { width: 720px; right: auto; left: 50%; transform: translateX(-50%); border: 1px solid rgba(31, 94, 65, 0.13); border-bottom: 0; border-radius: 24px 24px 0 0; }
}

@media (max-width: 360px) {
  .asset-header, .asset-main { width: min(100% - 24px, 720px); }
  .stats-grid strong { font-size: 15px; }
}
</style>
